import { useEffect, useRef, useState } from 'react';
import {
  IDLE_ACTIVITY_EVENTS,
  IDLE_TICK_MS,
  IDLE_WARN_AFTER_MS,
} from './sessionTimeout.config';

/**
 * Tracks browser-side user inactivity. Returns a snapshot of how
 * many milliseconds have elapsed since the last user-input event,
 * plus a `reset()` callback the caller can fire after a successful
 * token refresh.
 *
 * Implementation notes:
 *   - The activity-event listeners are PASSIVE and update a ref —
 *     they NEVER trigger a re-render. Only the once-per-second tick
 *     causes a state update, and only when the bucket changes
 *     (active vs idle) to keep React quiet during normal use.
 *   - Listeners attach to `window` so events from any iframe / portal
 *     still count.
 *   - When `enabled` flips false (user logs out) the hook unwires
 *     itself completely; no leaked intervals.
 *
 * Bug ref: AI Agent 2026-06-11 — "session time out and inactivity
 * popup, click OK to continue otherwise logout".
 */
export interface IdleTimerHandle {
  /** Milliseconds since the last detected user-input event. */
  idleMs: number;
  /**
   * True when `idleMs >= IDLE_WARN_AFTER_MS`. Caller uses this to
   * open the timeout-warning modal.
   */
  isIdle: boolean;
  /**
   * Manually mark the user as "just active" — e.g. after a
   * successful token refresh, so the countdown doesn't immediately
   * re-fire.
   */
  reset: () => void;
}

export function useIdleTimer(enabled: boolean): IdleTimerHandle {
  const lastActiveRef = useRef<number>(Date.now());
  const [idleMs, setIdleMs] = useState(0);
  /* We only update state when the idle bucket changes so steady-state
     activity doesn't cause a re-render every second. */
  const wasIdleRef = useRef<boolean>(false);

  useEffect(() => {
    if (!enabled) {
      setIdleMs(0);
      lastActiveRef.current = Date.now();
      wasIdleRef.current = false;
      return;
    }

    const handler = () => {
      lastActiveRef.current = Date.now();
    };

    // Attach passive listeners — never block scroll / input.
    for (const ev of IDLE_ACTIVITY_EVENTS) {
      window.addEventListener(ev, handler, { passive: true });
    }

    const tick = window.setInterval(() => {
      const elapsed = Date.now() - lastActiveRef.current;
      const idleNow = elapsed >= IDLE_WARN_AFTER_MS;
      /* Update state in two cases:
         1. Bucket flipped (active ⇆ idle) — modal needs to (un)open.
         2. We're currently idle — countdown display needs the live ms. */
      if (idleNow || wasIdleRef.current !== idleNow) {
        setIdleMs(elapsed);
        wasIdleRef.current = idleNow;
      }
    }, IDLE_TICK_MS);

    return () => {
      for (const ev of IDLE_ACTIVITY_EVENTS) {
        window.removeEventListener(ev, handler);
      }
      window.clearInterval(tick);
    };
  }, [enabled]);

  return {
    idleMs,
    isIdle: idleMs >= IDLE_WARN_AFTER_MS,
    reset: () => {
      lastActiveRef.current = Date.now();
      setIdleMs(0);
      wasIdleRef.current = false;
    },
  };
}
